import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withState,
  withMethods,
  withComputed,
  getState,
} from '@ngrx/signals';
import { WegweiserApiService } from '../../services/wegweiser-api.service';
import {
  CustomGridStackWidget,
  WegweiserData,
  WegweiserIndicator,
  WegweiserRegion,
  WegweiserRequestParams,
  WidgetConfig,
} from '../../services/grid-widget-config.service';
import { TreeNode } from 'primeng/api';
import { WidgetStore } from '../grid/widget.state';

/**
 * Interface representing the state of indicators
 */
type IndicatorsState = {
  indicators: WegweiserIndicator[];
  topics: any[];
  regions: WegweiserRegion[];
  regionCache: WegweiserRegion[];
  geoData: any[];
  geoCache: Record<number, any>;
  data: any;
  isLoading: boolean;
  error: string | null;
};

/** Initial state */
const initialState: IndicatorsState = {
  indicators: [],
  topics: [],
  regions: [],
  regionCache: [],
  geoData: [],
  geoCache: {},
  data: [],
  isLoading: false,
  error: null,
};

export const WegweiserStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ topics, regionCache }) => ({
    keyValueIndicators: computed(() => {
      const indicators = topics().reduce(
        (acc, topic) => {
          topic.indicators.forEach((indicator: any) => {
            indicator.statement = indicator.explanation;
            acc[String(indicator.id)] = indicator;
          });
          return acc;
        },
        {} as Record<string, any>
      );

      return indicators;
    }),
    keyValueRegions: computed(() => {
      return regionCache().reduce(
        (acc, region) => {
          acc[String(region.id)] = region;

          return acc;
        },
        {} as Record<string, any>
      );
    }),
    indicatorsAsTree: computed(() => {
      //const grouped = Object.values(items()).reduce(
      const tree: TreeNode[] = topics()
        .map((topic: any) => {
          return {
            _id: String(topic.id),
            key: String(topic.id),
            icon: 'fas fa-folder',
            type: 'category',
            expandedIcon: 'fas fa-folder-open',
            collapsedIcon: 'fas fa-folder',
            label: topic.name,
            children: topic.indicators.map((indicator: any) => {
              return {
                type: 'indicator',
                key: String(indicator.id),
                //key: `${topic.id}_${indicator._id}`,
                _id: String(indicator.id),
                collapsedIcon: 'fas fa-ruler-vertical',
                label: indicator.name,
              };
            }),
          };
        })
        .filter(node => node.children.length);
      return JSON.parse(JSON.stringify(tree)) as TreeNode[];
    }),
  })),
  withMethods(
    (
      store,
      wegweiserService = inject(WegweiserApiService),
      widgetStore = inject(WidgetStore)
    ) => {
      return {
        async restoreByWidget(wegweiserData: WegweiserData) {
          const currentState = getState(store);
          currentState.regions = [
            ...currentState.regions,
            ...wegweiserData.regions,
          ];
          currentState.regionCache = [
            ...currentState.regions,
            ...wegweiserData.regions,
          ];
          currentState.indicators = [
            ...currentState.indicators,
            ...wegweiserData.indicators,
          ];
          if (wegweiserData.geoData) {
            wegweiserData.geoData.features.forEach((feature: any) => {
              currentState.geoCache[feature.id] = feature;
            });
          }
          patchState(store, currentState);
        },
        /**
         * Fetch indicators from API and update state.
         */
        async fetchIndicators(searchString: string): Promise<void> {
          patchState(store, { isLoading: true, error: null });

          try {
            const indicators =
              await wegweiserService.getIndicators(searchString);
            patchState(store, { indicators, isLoading: false });
          } catch (error: any) {
            patchState(store, { error: error.message, isLoading: false });
          }
        },

        /**
         * Fetch indicators from API and update state.
         */
        async fetchTopics(): Promise<void> {
          patchState(store, { isLoading: true, error: null });

          try {
            const topics = await wegweiserService.getTopicSets();
            patchState(store, {
              topics: topics.at(0).topics,
              isLoading: false,
            });
          } catch (error: any) {
            patchState(store, { error: error.message, isLoading: false });
          }
        },

        /**
         * Fetch regions from API and update state.
         */
        async fetchRegions(searchString: string): Promise<void> {
          patchState(store, { isLoading: true, error: null });

          try {
            const regions = await wegweiserService.getRegions(searchString);
            patchState(store, { regions, isLoading: false });
          } catch (error: any) {
            patchState(store, { error: error.message, isLoading: false });
          }
        },

        /**
         * Fetch geoData from API and update state.
         */
        async fetchGeoData(region: WegweiserRegion): Promise<void> {
          patchState(store, { isLoading: true, error: null });
          const fromCache = store.geoCache()[region.id];

          if (fromCache) return;
          try {
            const geoData = await wegweiserService.getGeoDataByRegionName(
              region.friendlyUrl,
              region.type
            );
            const geoCache = store.geoCache();
            //geoCache[region.id] = geoData;
            geoData.regions.features.forEach((feature: any) => {
              geoCache[feature.id] = feature;
            });
            patchState(store, { geoData, geoCache, isLoading: false });
          } catch (error: any) {
            patchState(store, { error: error.message, isLoading: false });
          }
        },

        /**
         * Retrieves geographic data for the given region IDs from the cache.
         *
         * @param {number[]} regionIds - An array of region IDs to retrieve geographic data for.
         * @return {Array} An array of geographic data objects corresponding to the provided region IDs. Null values are filtered out.
         */
        getGeoDataByRegionIds(regionIds: number[]): Array<any> {
          const cache = store.geoCache();
          return regionIds
            .map(regionId => {
              return cache[regionId] || null;
            })
            .filter(data => data !== null);
        },

        /**
         * find Data by stuff from API and update state.
         */
        async findData(requestParams: WegweiserRequestParams): Promise<void> {
          patchState(store, { isLoading: true, error: null });
          try {
            const data = await wegweiserService.getData(requestParams);
            patchState(store, { data, isLoading: false });
          } catch (error: any) {
            patchState(store, { error: error.message, isLoading: false });
          }
        },

        /**
         * cache region found
         * @param region
         */
        cacheRegion(region: WegweiserRegion) {
          const regionCache = store.regionCache();
          const index = regionCache.findIndex(
            cacheRegion => cacheRegion.id === region.id
          );

          if (index === -1) {
            const newRegionCache = [...regionCache, region]; // Use a new array reference
            patchState(store, { regionCache: newRegionCache });
          }
        },

        /**
         * get wegweiser-indicator by id as key of key-value-indicators (computed)
         * @param id
         */
        getWegweiserIndicatorById(id: string) {
          return store.keyValueIndicators()[id];
        },

        /**
         * get wegweiser-region by id as key of key-value-regions (computed)
         * @param id
         */
        getWegweiserRegionById(id: number) {
          return store.keyValueRegions()[id];
        },

        /**
         * Retrieves geographical data for the given region IDs.
         *
         * @param {number[]} regionIds - An array of region IDs for which geographical data is to be fetched.
         * @return {Object} Returns a GeoJSON object containing a FeatureCollection with the corresponding features.
         */
        getGeoDataByIds(regionIds: number[]) {
          let features: any[] = [];
          regionIds.forEach(id => {
            const regions = store.geoCache()[id] || null;
            if (regions) {
              features = features.concat(regions);
            }
          });

          return {
            type: 'FeatureCollection',
            features: features,
          };
        },

        /**
         * get all overlapping years of given indicator-ids
         * @param indicatorIds
         */
        getCommonYearsByIndicators(indicatorIds: number[]) {
          const firstId = indicatorIds.at(0);
          if (!firstId || !indicatorIds.length) return [];
          return [
            ...indicatorIds
              .map(id => {
                const indicator = store.keyValueIndicators()[String(id)];
                return new Set<number>(indicator.years);
              })
              .reduce(
                (commonYears: Set<number>, indicatorYears: Set<number>) =>
                  new Set(
                    [...commonYears].filter(year => indicatorYears.has(year))
                  ), // Keep only common years
                new Set<number>(
                  store.keyValueIndicators()[String(firstId)].years
                )
              ),
          ];
        },
        getRegionsMap(data: WegweiserData) {
          return data.regions.reduce(
            (acc: any, region: any) => {
              acc[String(region.id)] = region;

              return acc;
            },
            {} as Record<string, WegweiserRegion>
          );
        },
        setUpWegweiserWidgetByLayout(widget: CustomGridStackWidget) {
          if (!widget.widget.wegweiserData) return;
          const data = widget.widget.wegweiserData;
          widget.widget.features = this.getRegionsMap(data);
          widgetStore.update(widget._id, widget, true);
        },
        updateWegweiserDataAndWriteToWidget(
          widget: CustomGridStackWidget
        ): void {
          if (!widget.widget) return;
          if (
            widget.widget.wegweiserData &&
            (!widget.widget.wegweiserRequestParams.indicatorIds.length ||
              !widget.widget.wegweiserRequestParams.regionIds.length)
          ) {
            widget.widget.indicator = '';
            widget.widget.indicators = [];
            widget.widget.wegweiserData.indicators = [];

            widgetStore.update(widget._id, widget, true);
          } else {
            this.findData(widget.widget.wegweiserRequestParams).then(() => {
              const data = store.data();
              const years = widget.widget.wegweiserRequestParams.years;
              const result = {
                indicators: data.indicators,
                regions: data.regions,
                years: widget.widget.wegweiserRequestParams.years,
                geoData: widget.widget.wegweiserData?.geoData || null,
              };
              widget.widget.wegweiserData = result;
              widget.widget.year = String(years[years.length - 1]);
              widget.widget.indicator = String(data.indicators.at(0).id);
              widget.widget.indicators = data.indicators.map((indicator: any) =>
                String(indicator.id)
              );
              //widget.widget.features = data.regions;
              widget.widget.features = this.getRegionsMap(data);
              widget.widget.feature = String(data.regions.at(0).id);
              widgetStore.update(widget._id, widget, true);
            });
          }
        },
        getWidgetCaption(config: WidgetConfig) {
          switch (config.focusProperty) {
            case 'indicator':
              return this.getIndicatorCaption(config.indicator);
            case 'dataLayer':
              return this.getYearCaption(config.year);
            case 'feature':
              return this.getRegionCaption(Number(config.feature));
            default:
              return '';
          }
        },
        getIndicatorCaption(id: string) {
          const indicator = this.getWegweiserIndicatorById(id);
          if (!indicator) return '';
          return `Wegweiser Kommune: ${indicator.name ?? ''}`;
        },
        getRegionCaption(id: number) {
          const region = this.getWegweiserRegionById(id);
          if (!region) return '';
          return `Wegweiser Kommune: ${region.name ?? ''}`;
        },
        getYearCaption(year: string) {
          return `Wegweiser Kommune: ${year}`;
        },
      };
    }
  )
);
